'use client';

import { useState, type FormEvent } from 'react';
import { getAttribution } from '@/lib/analytics/attribution';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Globe, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import styles from './UrlAuditForm.module.css';

interface UrlAuditFormProps {
  locale: string;
}

export function UrlAuditForm({ locale }: UrlAuditFormProps) {
  const tHero = useTranslations('audit.hero');
  const t = useTranslations('urlAuditForm');
  const router = useRouter();

  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fullUrl = domain.trim() ? `https://${domain.trim()}` : '';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!domain.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/audit/free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: fullUrl, attribution: getAttribution() }),
      });

      const data = await response.json();

      if (data.success && data.reportId) {
        router.push(`/${locale}/audit/${data.reportId}${data.accessToken ? `?t=${encodeURIComponent(data.accessToken)}` : ''}`);
      } else {
        setError(data.error || t('errorAuditFailed'));
      }
    } catch {
      setError(t('errorNetwork'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.divider} />

      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('title')}</h2>
          <p className={styles.subtitle}>{t('subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputWrapper}>
            <Globe size={18} className={styles.inputIcon} />
            <span className={styles.prefix}>https://</span>
            <input
              type="text"
              className={styles.input}
              placeholder={t('placeholder')}
              value={domain}
              onChange={(e) => setDomain(e.target.value.replace(/^https?:\/\//, ''))}
              disabled={isLoading}
              required
            />
          </div>
          <Button type="submit" size="lg" disabled={isLoading || !domain.trim()}>
            {isLoading ? (
              <>
                <Loader2 size={16} className={styles.spinner} />
                {tHero('urlInput.loading')}
              </>
            ) : (
              tHero('urlInput.button')
            )}
          </Button>
        </form>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <p className={styles.hint}>{tHero('urlInput.hint')}</p>
      </div>
    </div>
  );
}
